// Food.jsx
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// === IMAGES === (use your real paths)
import heroBg from '../assets/Mahi/64.jpeg';
import ayurvedicFoodImage1 from '../assets/Mahi/55.jpeg';
import ayurvedicFoodImage2 from '../assets/Mahi/100.jpeg';
import ayuFood from '../assets/Mahi/103.jpeg';
import ayuFood2 from '../assets/Mahi/87.jpeg';


const sectionFade = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 1, ease: "easeOut" } },
};

const staggerContainer = {
    hidden: { opacity: 1 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.2, delayChildren: 0.15 },
    },
};

const childItem = {
    hidden: { opacity: 0, y: 25 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const Food = () => {
    const { t } = useTranslation('food');

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const ayurvedicPhilosophy = [
        t('philosophyText1'),
        t('philosophyText2'),
        t('philosophyText3'),
        t('philosophyText4'),
        t('philosophyText5'),
        t('philosophyText6'),
        t('philosophyText7'),
        t('philosophyText8'),
    ];



    return (
        <div className="bg-[#F8F5ED] text-[#2D3F3A] font-sans min-h-screen">
            {/* ====================== HERO ====================== */}
            <section className="relative h-[70vh] min-h-[500px] flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src={heroBg}
                        alt={t('heroAlt')}
                        className="w-full h-full object-cover brightness-[0.75] scale-105 transition-transform duration-[20s]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60" />
                </div>

                <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
                    <motion.h1
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.2, ease: "easeOut" }}
                        className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-serif font-light tracking-wide text-white leading-tight drop-shadow-2xl"
                    >
                        {t('heroTitle')}
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.2, delay: 0.4 }}
                        className="mt-6 text-xl sm:text-2xl md:text-3xl font-light text-[#E8C07D] tracking-wider"
                    >
                        {t('heroSubtitle')}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1.4, delay: 0.7 }}
                        className="mt-10 inline-flex items-center gap-3"
                    >
                        <div className="h-px w-16 bg-[#E8C07D]/70" />
                        <span className="text-[#E8C07D] uppercase tracking-widest text-sm font-medium">
                            {t('wellnessEyebrow')}
                        </span>
                        <div className="h-px w-16 bg-[#E8C07D]/70" />
                    </motion.div>
                </div>
            </section>

            {/* ====================== AYURVEDIC FOOD PHILOSOPHY (sm only) ====================== */}
            <section className="py-16 sm:py-20 bg-gradient-to-b from-[#F8F5ED] to-[#F0EDE3] block md:hidden">
                <div className="container mx-auto px-5 sm:px-8">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-100px" }}
                        variants={staggerContainer}
                        className="text-center mb-10"
                    >
                        <motion.span
                            variants={childItem}
                            className="inline-block text-[#E8C07D] uppercase tracking-widest text-xs font-medium mb-3"
                        >
                            {t('wellnessEyebrow')}
                        </motion.span>
                        <motion.h2
                            variants={childItem}
                            className="text-3xl sm:text-4xl font-serif font-light text-[#1A2F2A] leading-tight"
                        >
                            {t('wellnessTitle')} <span className="text-[#E8C07D] font-normal">{t('ayurvedicFood')}</span>
                        </motion.h2>
                        <motion.div variants={childItem} className="h-1 w-16 mx-auto mt-5 bg-gradient-to-r from-[#E8C07D] to-[#4A7C76] rounded-full" />
                    </motion.div>

                    <div className="space-y-6">
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={sectionFade}
                            className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[4/3]"
                        >
                            <img
                                src={ayurvedicFoodImage1}
                                alt={t('foodImage1Alt')}
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                        </motion.div>

                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={sectionFade}
                            className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[4/3]"
                        >
                            <img
                                src={ayurvedicFoodImage2}
                                alt={t('foodImage2Alt')}
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                        </motion.div>

                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={staggerContainer}
                            className="space-y-4"
                        >
                            {[t('wellnessIntro1'), t('wellnessIntro2'), t('wellnessIntro3')].map((text, i) => (
                                <motion.p
                                    key={i}
                                    variants={childItem}
                                    className="text-base sm:text-lg leading-relaxed text-[#2D3F3A]/90 font-light border-l-4 border-[#E8C07D]/60 pl-5 py-1"
                                >
                                    {text}
                                </motion.p>
                            ))}
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* ====================== AYURVEDIC FOOD PHILOSOPHY ====================== */}
            <section className="py-16 md:py-24 lg:py-32 bg-gradient-to-b from-[#F8F5ED] to-[#F0EDE3] hidden md:block">
                <div className="container mx-auto px-5 sm:px-8 lg:px-12 ">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-100px" }}
                        variants={staggerContainer}
                        className="text-center mb-16 md:mb-20"
                    >
                        <motion.span
                            variants={childItem}
                            className="inline-block text-[#E8C07D] uppercase tracking-widest text-sm md:text-base font-medium mb-4"
                        >
                            {t('wellnessEyebrow')}
                        </motion.span>
                        <motion.h2
                            variants={childItem}
                            className="text-4xl md:text-5xl lg:text-6xl font-serif font-light text-[#1A2F2A] leading-tight"
                        >
                            {t('wellnessTitle')} <span className="text-[#E8C07D] font-normal">{t('ayurvedicFood')}</span>
                        </motion.h2>
                        <motion.div variants={childItem} className="h-1 w-24 mx-auto mt-6 bg-gradient-to-r from-[#E8C07D] to-[#4A7C76] rounded-full" />
                    </motion.div>

                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                        {/* Images – elegant overlap */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={sectionFade}
                            className="relative order-2 lg:order-1"
                        >
                            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                                <img
                                    src={ayurvedicFoodImage1}
                                    alt={t('foodImage1Alt')}
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                                />
                            </div>

                            <div className="absolute -bottom-12 -right-6 sm:-right-12 w-3/5 sm:w-2/3 aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl border-8 border-white/90 rotate-3 hover:rotate-0 transition-transform duration-700">
                                <img
                                    src={ayurvedicFoodImage2}
                                    alt={t('foodImage2Alt')}
                                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                                />
                            </div>
                        </motion.div>

                        {/* Text */}
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={staggerContainer}
                            className="space-y-8 order-1 lg:order-2"
                        >
                            {[t('wellnessIntro1'), t('wellnessIntro2'), t('wellnessIntro3')].map((text, i) => (
                                <motion.p
                                    key={i}
                                    variants={childItem}
                                    className="text-lg md:text-xl leading-relaxed text-[#2D3F3A]/90 font-light border-l-4 border-[#E8C07D]/60 pl-6 py-1"
                                >
                                    {text}
                                </motion.p>
                            ))}
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* ====================== NUTRITION & PHILOSOPHY (sm only) ====================== */}
            <section className="py-16 sm:py-20 bg-[#F0EDE3] block md:hidden">
                <div className="container mx-auto px-5 sm:px-8">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={staggerContainer}
                        className="text-center mb-10"
                    >
                        <motion.span variants={childItem} className="text-[#E8C07D] uppercase tracking-widest text-xs font-medium">
                            {t('culinaryPhilosophyEyebrow')}
                        </motion.span>
                        <motion.h2 variants={childItem} className="mt-3 text-3xl sm:text-4xl font-serif font-light text-[#1A2F2A]">
                            {t('culinaryPhilosophyTitle')} <span className="text-[#E8C07D]">{t('nutritionHighlight')}</span>
                        </motion.h2>
                        <motion.div variants={childItem} className="h-1 w-16 mx-auto mt-5 bg-gradient-to-r from-[#E8C07D] to-[#4A7C76] rounded-full" />
                    </motion.div>

                    <div className="space-y-6">
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={sectionFade}
                            className="space-y-6"
                        >
                            {[
                                { src: ayuFood, alt: t('nutritionTopImageAlt'), label: t('freshNourishingLabel') },
                                { src: ayuFood2, alt: t('healingIngredientsAlt'), label: t('healingIngredientsLabel') },
                            ].map((img, idx) => (
                                <div key={idx} className="group relative rounded-2xl overflow-hidden shadow-xl">
                                    <div className="absolute inset-0 bg-gradient-to-br from-[#E8C07D]/20 to-[#4A7C76]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                                    <img
                                        src={img.src}
                                        alt={img.alt}
                                        className="w-full aspect-[4/3] object-cover transition-transform duration-900 group-hover:scale-110"
                                    />
                                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-5">
                                        <p className="text-[#E8C07D] font-medium text-base sm:text-lg">{img.label}</p>
                                    </div>
                                </div>
                            ))}
                        </motion.div>

                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={staggerContainer}
                            className="space-y-5"
                        >
                            {ayurvedicPhilosophy.map((point, i) => (
                                <motion.div
                                    key={i}
                                    variants={childItem}
                                    className="flex gap-4 group"
                                >
                                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#E8C07D] to-[#4A7C76] flex items-center justify-center text-white font-semibold text-base shadow-md group-hover:scale-110 transition-transform">
                                        {i + 1}
                                    </div>
                                    <p className="text-base sm:text-lg leading-relaxed text-[#2D3F3A]/90 font-light group-hover:text-[#1A2F2A] transition-colors">
                                        {point}
                                    </p>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* ====================== NUTRITION & PHILOSOPHY ====================== */}
            <section className="py-20 md:py-28 lg:py-36 bg-[#F0EDE3] hidden md:block">
                <div className="container mx-auto px-5 sm:px-8 lg:px-12 ">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={staggerContainer}
                        className="text-center mb-16 md:mb-20"
                    >
                        <motion.span variants={childItem} className="text-[#E8C07D] uppercase tracking-widest text-sm md:text-base font-medium">
                            {t('culinaryPhilosophyEyebrow')}
                        </motion.span>
                        <motion.h2 variants={childItem} className="mt-3 text-4xl md:text-5xl lg:text-6xl font-serif font-light text-[#1A2F2A]">
                            {t('culinaryPhilosophyTitle')} <span className="text-[#E8C07D]">{t('nutritionHighlight')}</span>
                        </motion.h2>
                        <motion.div variants={childItem} className="h-1 w-24 mx-auto mt-6 bg-gradient-to-r from-[#E8C07D] to-[#4A7C76] rounded-full" />
                    </motion.div>

                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
                        {/* Images column */}
                        <motion.div variants={sectionFade} className="space-y-10 order-2 lg:order-1">
                            {[
                                { src: ayuFood, alt: t('nutritionTopImageAlt'), label: t('freshNourishingLabel') },
                                { src: ayuFood2, alt: t('healingIngredientsAlt'), label: t('healingIngredientsLabel') },
                            ].map((img, idx) => (
                                <div key={idx} className="group relative rounded-2xl overflow-hidden shadow-xl">
                                    <div className="absolute inset-0 bg-gradient-to-br from-[#E8C07D]/20 to-[#4A7C76]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                                    <img
                                        src={img.src}
                                        alt={img.alt}
                                        className="w-full aspect-[4/3] object-cover transition-transform duration-900 group-hover:scale-110"
                                    />
                                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                                        <p className="text-[#E8C07D] font-medium text-lg">{img.label}</p>
                                    </div>
                                </div>
                            ))}
                        </motion.div>

                        {/* Philosophy list */}
                        <motion.div variants={staggerContainer} className="space-y-8 order-1 lg:order-2">
                            {ayurvedicPhilosophy.map((point, i) => (
                                <motion.div
                                    key={i}
                                    variants={childItem}
                                    className="flex gap-5 group"
                                >
                                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-[#E8C07D] to-[#4A7C76] flex items-center justify-center text-white font-semibold text-xl shadow-md group-hover:scale-110 transition-transform">
                                        {i + 1}
                                    </div>
                                    <p className="text-lg leading-relaxed text-[#2D3F3A]/90 font-light group-hover:text-[#1A2F2A] transition-colors">
                                        {point}
                                    </p>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </div>
            </section>

        </div>
    );
};

export default Food;